const DEFAULT_IMG_WIDTH = 600;

/**
 * HOC Filter Function to find the Image with the Primary Indicator.
 * @param image [object] The Image object instance from the Catalog Entity Object.
 * @returns primaryInd [boolean] The Flag indicating if the current image is the primary image for the current entity.
 *
 */
const primaryHeroImageFilter = (image) => {
  return image && image.primaryInd;
};

/**
 * Check whether current performer belongs to target grouping
 * Or whether current grouping is target grouping
 * @param {array} groupings
 * @param {number} targetGroupingId
 * @param {number} entityId
 */
const specificGroupingCheck = (groupings = [], targetGroupingId, entityId) => {
  return (
    entityId === targetGroupingId ||
    groupings.some((element) => element && element.id && element.id === targetGroupingId)
  );
};

export const ENTITY_TYPE_PERFORMER = 'performer';
export const ENTITY_TYPE_PERFORMER_TEAM = 'team';
export const ENTITY_TYPE_PERFORMER_ARTIST = 'artist';
export const ENTITY_TYPE_CITYPAGE = 'CityPage';
export const ENTITY_TYPE_CITY = 'city';
export const ENTITY_TYPE_VENUE = 'venue';
export const ENTITY_TYPE_CATEGORY = 'category';
export const ENTITY_TYPE_GROUPING = 'grouping';
export const ENTITY_TYPE_GEO = 'geography';
export const ENTITY_TYPE_EVENT = 'event';

/**
 * Catalog Categories Map
 *
 */
export const staticCategory = {
  concert: 1,
  sports: 28,
  theater: 174,
};

/**
 * Catalog Groupings Map
 *
 */
export const staticGroupings = {
  mlb: 81,
  nba: 115,
  nfl: 121,
  mls: 142,
  nhl: 144,
  college_football: 122,
  ncaa_basketball: 333,
  ncaa_baseball: 8148,
};

/**
 * Method to get the Category and Grouping Attributes for a Given Entity.
 * @param entity [object={}] The Performer object that is instance of the Catalog Performer API Response.
 *
 */
export const getCatGrpAttributes = (entity = {}) => {
  const {categories = [], groupings = []} = entity.ancestors || entity || {};

  /**
   * Parent Category
   *
   */
  const parentCategory = (categories[0] && categories[0].id ? categories[0] : categories[1]) || {};
  const parentCategoryId = parentCategory.id;
  const parentCategoryName = parentCategory.name;

  /**
   * Parent Grouping
   *
   */
  const parentGrouping = (groupings[0] && groupings[0].id ? groupings[0] : groupings[1]) || {};
  const parentGroupingId = parentGrouping.id;
  const parentGroupingName = parentGrouping.name;

  /**
   * Sport Categories Check
   *
   */
  const isSport = parentCategoryId === staticCategory.sports;
  const isMLB = isSport && specificGroupingCheck(groupings, staticGroupings.mlb, entity.id);
  // @TODO why are we using isMLB here? Could isSport work just as well in place
  const isNFL = !isMLB && specificGroupingCheck(groupings, staticGroupings.nfl, entity.id);
  const isNBA = isSport && specificGroupingCheck(groupings, staticGroupings.nba, entity.id);
  const isMLS = isSport && specificGroupingCheck(groupings, staticGroupings.mls, entity.id);
  const isNHL = isSport && specificGroupingCheck(groupings, staticGroupings.nhl, entity.id);
  const isNCAAFootball = isSport && specificGroupingCheck(groupings, staticGroupings.college_football, entity.id);
  const isNCAABasketball = isSport && specificGroupingCheck(groupings, staticGroupings.ncaa_basketball, entity.id);
  const isNCAABaseball = isSport && specificGroupingCheck(groupings, staticGroupings.ncaa_baseball, entity.id);

  /**
   * Artist Category Check
   *
   */
  const isArtist = !isSport;

  /**
   * Concert Category Check
   *
   */
  const isConcert = !isSport && parentCategoryId === staticCategory.concert;

  /**
   * Theater Category Check
   *
   */
  const isTheatre = !isSport && parentCategoryId === staticCategory.theater;

  return {
    isSport,
    isConcert,
    isTheatre,
    isArtist,
    isMLB,
    isNFL,
    isNBA,
    isMLS,
    isNHL,
    isNCAAFootball,
    isNCAABasketball,
    isNCAABaseball,
    parentCategoryId,
    parentCategoryName,
    parentGroupingId,
    parentGroupingName,
  };
};

/**
 * Method to Extract the SEO Meta object from the Catalog Performer Object.
 * @param entity [object={}] The Performer object that is instance of the Catalog Performer API Response
 * @returns seoMeta [object={}] SEO Meta Data object extracted from the Performer.
 *
 */
export const getEntitySEOObj = (entity = {}) => {
  const {seoMeta = {}} = entity;

  return seoMeta;
};

/**
 * Method to the get the entity sub type for the Catalog Entity
 * @param entityType [string] The Type of the entity that is to be processed (Performer, Category, Grouping, Venue).
 * @param entity [object] The Catalog API response instance for the `entityType`.
 *
 */

export const isCity = (entity = {}) => {
  let isCityPageValidAttribute = false;
  const cityHeaderAttr =
    entity.dynamicAttributes && entity.dynamicAttributes.find((item) => item.name === 'header_copy');
  if (cityHeaderAttr) {
    isCityPageValidAttribute = true;
  }

  return isCityPageValidAttribute;
};

export const getEntitySubType = (entityType, entity = {}) => {
  let entitySubType = entityType;
  const isCityPage = isCity(entity);

  if (ENTITY_TYPE_PERFORMER === entityType) {
    entitySubType = entity.isSport ? ENTITY_TYPE_PERFORMER_TEAM : ENTITY_TYPE_PERFORMER_ARTIST;
  } else if (ENTITY_TYPE_GEO === entityType && isCityPage) {
    entitySubType = ENTITY_TYPE_CITYPAGE;
  }

  return entitySubType;
};

/**
 * Method to get the Performer SEO Title
 * @param performer [object={}] The Performer object that is instance of the Catalog Performer API Response
 * @return SeoTitle [String] The SeoTitle for the given performer
 *
 */
export const getEntitySeoTitle = (performer = {}) => {
  const performerSEOObj = getEntitySEOObj(performer);

  return performerSEOObj.title || performerSEOObj.seoTitle;
};

/**
 * Method to get the Search Catalog Events API Search Specification for the EntityType.
 * @param entityType [string] The Type of the entity that is to be processed (Performer, Category, Grouping, Venue).
 * @param entityId [object] The Id of the current Entity.
 * @returns [object] The Search Specification that can be passed to the Search Catalog Events API.
 *
 */
export const getEntitySearchAPIParams = (entityType, entityId) => {
  const entityIdFieldName = entityType ? `${entityType}Id` : '';
  let searchAPIParams = {};

  if (entityIdFieldName) {
    searchAPIParams = {
      [entityIdFieldName]: entityId,
    };
  }

  return searchAPIParams;
};

/**
 * Method to Extract Relevant Image to be used in the header for the selected entity.
 * @param entityType [String] The Type of the entity in the page context.
 * @param entity [Object={}] The Entity object that is instance of the Catalog Entity API Response
 * @param options [Object={}] Options to logic details of image operation
 * @param options.noCompress [Boolean] Switch to reset image resolution to lower level, by default false
 * @returns image [object] The Image object that can be used as Hero Image for the entity.
 *
 */
export const getHeroImage = (entityType, entity = {}, options = {}) => {
  const {images} = entity;
  let image = {};

  if (images && images.length) {
    /**
     * Check for Primary Hero Image
     *
     */
    [image] = images.filter(primaryHeroImageFilter);

    /**
     * Default Image would be the First Image in the array
     *
     */
    if (!image) {
      [image] = images;
    }
    const {url = ''} = image || {};

    /**
     * @TODO REMOVE_THIS - Temporary fix to reduce image size until Cloudinary is consumed at the Catalog.
     *
     */
    if (!options.noCompress && url) {
      image.url = url.replace('l1600', 'l600');
    }
  }

  /**
   * Mapbox Image for Venue without a Default Image.
   *
   */
  if (!image.url && entityType === ENTITY_TYPE_VENUE) {
    const {latitude} = entity;
    const {longitude} = entity;
    const currentWindowWidth = __CLIENT__ ? window.innerWidth : DEFAULT_IMG_WIDTH;
    const imageWidth = Math.min(currentWindowWidth, DEFAULT_IMG_WIDTH);
    const imageHeight = '220';
    const zoomBearingPitch = '15.00,0.00,0.00';
    const accessToken = 'pk.eyJ1Ijoicmlqb2huc3RvbiIsImEiOiJVWFVxLWZFIn0.R0i8_CtlciPDpRw5v3G86A';
    const style = 'rijohnston/cj63vn2x84xta2rmz1stwwfdf';
    const markerColor = '085F99';
    const marker = `pin-l+${markerColor}(${longitude},${latitude})`;

    image.url = `https://api.mapbox.com/styles/v1/${style}/static/${marker}/${longitude},${latitude},${zoomBearingPitch}/${imageWidth}x${imageHeight}?access_token=${accessToken}`;
  }

  if (images && images.length && !image.url) {
    image.url = '';
  }

  return image;
};

/**
 * Method to validate if the current entity type is PERFORMER.
 * @param entityType [String] The Type of the entity in the page context.
 * @returns isPerformerEntity [boolean] Flag indicating if the current entity type is a Performer.
 * @TODO Pass entity object instead of the entityType to determin the type of entity.
 *
 */
export const isPerformerEntity = (entityType) => {
  return entityType === ENTITY_TYPE_PERFORMER;
};

/**
 * Method to validate if the current entity subtype is TEAM.
 * @param entitySubType [String] The Type of the entity in the page context.
 * @returns isTeamPerformerEntity [boolean] Flag indicating if the current entity type is a TEAM Performer.
 * @TODO Pass entity object instead of the entityType to determin the type of entity.
 *
 */
export const isTeamPerformerEntity = (entitySubType) => {
  return entitySubType === ENTITY_TYPE_PERFORMER_TEAM;
};

/**
 * Method to validate if the current entity type is GROUPING.
 * @param entityType [String] The Type of the entity in the page context.
 * @returns isGroupingEntity [boolean] Flag indicating if the current entity type is a GROUPING.
 * @TODO Pass entity object instead of the entityType to determin the type of entity.
 *
 */
export const isGroupingEntity = (entityType) => {
  return entityType === ENTITY_TYPE_GROUPING;
};

/**
 * Method to validate if the current entity type is CATEGORY.
 * @param entityType [String] The Type of the entity in the page context.
 * @returns isGroupingEntity [boolean] Flag indicating if the current entity type is a CATEGORY.
 * @TODO Pass entity object instead of the entityType to determin the type of entity.
 *
 */
export const isCategoryEntity = (entityType) => {
  return entityType === ENTITY_TYPE_CATEGORY;
};

/**
 * Method to validate if the current entity type is CATEGORY.
 * @param entityType [String] The Type of the entity in the page context.
 * @returns isGroupingEntity [boolean] Flag indicating if the current entity type is a CATEGORY.
 * @TODO Pass entity object instead of the entityType to determin the type of entity.
 *
 */
export const isVenueEntity = (entityType) => {
  return entityType === ENTITY_TYPE_VENUE;
};

/**
 * Method to validate if the current entity type is GEOGRAPHY.
 * @param entityType [String] The Type of the entity in the page context.
 * @returns isGeography [boolean] Flag indicating if the current entity type is a GEOGRAPHY.
 * @TODO Pass entity object instead of the entityType to determin the type of entity.
 *
 */
export const isGeography = (entityType) => {
  return entityType === ENTITY_TYPE_GEO;
};
